import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dropdown, Modal } from 'flowbite-react';
import Lottie from 'react-lottie-player';

import { useAllSelector } from '../../services/selectors/allSelector';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { getDriverDocument } from '../../services/apis/setting/getDriverDocument';
import { getPharmacyCurrentTime } from '../../utils/helper/helper';
import { maskHeaderText, shouldMaskPatientDetails } from '../../utils/helper/patientMasking';
import { normalizeBackendAssetUrl } from '../../utils/helper/url';

import { SOSemergencyEnableButton } from './SOSemergencyEnableButton';

import * as routeNames from '../../routes/routeName';
import idCardLottie from '../../assets/lottie/id-card.json';
import fallbackAvatar from '../../assets/images/userimg.png';

const OverflowMarquee = ({ text }) => {
	const containerRef = useRef(null);
	const textRef = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		const updateOverflowState = () => {
			if (!containerRef.current || !textRef.current) {
				return;
			}

			setIsOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
		};

		updateOverflowState();
		window.addEventListener('resize', updateOverflowState);
		const resizeObserver = typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(updateOverflowState)
			: null;

		if (resizeObserver && containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			window.removeEventListener('resize', updateOverflowState);
			resizeObserver?.disconnect();
		};
	}, [text]);

	if (!isOverflowing) {
		return (
			<div ref={containerRef} className="w-full overflow-hidden">
				<h1 ref={textRef} className="dashboard-pharmacy-name whitespace-nowrap text-[18px] font-black leading-[1.05] tracking-normal text-[#0f172a]">
					{text}
				</h1>
			</div>
		);
	}

	return (
		<div ref={containerRef} className="dashboard-marquee overflow-hidden">
			<div className="dashboard-marquee-track">
				<h1 ref={textRef} className="dashboard-pharmacy-name shrink-0 whitespace-nowrap pr-10 text-[18px] font-black leading-[1.05] tracking-normal text-[#0f172a]">
					{text}
				</h1>
				<h1 className="dashboard-pharmacy-name shrink-0 whitespace-nowrap pr-10 text-[18px] font-black leading-[1.05] tracking-normal text-[#0f172a]" aria-hidden="true">
					{text}
				</h1>
			</div>
		</div>
	);
};

export const TopBar = () => {
	const navigate = useNavigate();
	const { getDriverProfileResponse } = useAllSelector();
	const { driverSelectedPharmacy, authLoginResponse, pharmacyId } = useAuthSelector();

	const [documents, setDocuments] = useState({
		document_type_1: null,
		document_type_2: null,
	});
	const [currentDateAndTime, setCurrentDateAndTime] = useState(getPharmacyCurrentTime);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState(null);
	const [previewImageError, setPreviewImageError] = useState(false);
	const [previewImageLoading, setPreviewImageLoading] = useState(false);
	const previewImageRef = useRef(null);
	const previewRequestRef = useRef(null);

	useEffect(() => {
		if (authLoginResponse?.data?.data?.driver_profile) {
			setDocuments({
				document_type_1: authLoginResponse.data.data.driver_profile.document_type_1,
				document_type_2: authLoginResponse.data.data.driver_profile.document_type_2,
			});
		}

		if (getDriverProfileResponse?.data?.data) {
			setDocuments({
				document_type_1: getDriverProfileResponse.data.data.document_type_1,
				document_type_2: getDriverProfileResponse.data.data.document_type_2,
			});
		}
	}, [getDriverProfileResponse, authLoginResponse]);

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentDateAndTime(getPharmacyCurrentTime());
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => () => {
		previewRequestRef.current?.abort();
		if (previewImageRef.current) {
			URL.revokeObjectURL(previewImageRef.current);
		}
	}, []);

	const clearPreviewImage = () => {
		if (previewImageRef.current) {
			URL.revokeObjectURL(previewImageRef.current);
			previewImageRef.current = null;
		}
		setPreviewImage(null);
	};

	const handlePreviewClick = async (type) => {
		previewRequestRef.current?.abort();
		const requestController = new AbortController();
		previewRequestRef.current = requestController;
		clearPreviewImage();
		setPreviewImageError(false);
		setPreviewImageLoading(true);
		setIsPreviewOpen(true);

		try {
			const documentType = type === 'identity1' ? 1 : 2;
			const response = await getDriverDocument(documentType, requestController.signal);
			if (previewRequestRef.current !== requestController) {
				return;
			}
			const imageUrl = URL.createObjectURL(response.data);
			previewImageRef.current = imageUrl;
			setPreviewImage(imageUrl);
		} catch (error) {
			if (!requestController.signal.aborted) {
				setPreviewImageError(true);
			}
		} finally {
			if (previewRequestRef.current === requestController) {
				previewRequestRef.current = null;
				setPreviewImageLoading(false);
			}
		}
	};

	const closePreview = () => {
		previewRequestRef.current?.abort();
		previewRequestRef.current = null;
		setIsPreviewOpen(false);
		setPreviewImageLoading(false);
		clearPreviewImage();
	};

	const shouldMaskHeader = shouldMaskPatientDetails(pharmacyId);
	const pharmacyName = shouldMaskHeader
		? maskHeaderText(driverSelectedPharmacy?.pharmacies?.name) || 'PHARMACY NAME'
		: driverSelectedPharmacy?.pharmacies?.name?.toUpperCase() || 'PHARMACY NAME';
	const driverDisplayName =
		authLoginResponse?.data?.data?.driver_pharmacist_user?.name ||
		authLoginResponse?.data?.data?.driver_profile?.name ||
		authLoginResponse?.data?.data?.name ||
		'User Name';

	const driverProfilePhotoUrl = normalizeBackendAssetUrl(
		authLoginResponse?.data?.data?.driver_profile?.avatar
	);

	const photoTrigger = (
		<img
			className="h-full w-full cursor-pointer object-cover"
			src={driverProfilePhotoUrl || fallbackAvatar}
			alt="Driver profile"
			onError={(event) => {
				event.currentTarget.src = fallbackAvatar;
			}}
		/>
	);

	return (
		<>
			<div className="dashboard-topbar flex items-start mb-2 mt-1 gap-3 w-full">
				<div className="dashboard-topbar-avatar h-[64px] w-[64px] shrink-0 overflow-hidden rounded-[10px] bg-slate-200">
					{documents.document_type_1 || documents.document_type_2 ? (
						<Dropdown label={photoTrigger} dismissOnClick arrowIcon={false} inline className="w-fit">
							<div className="w-56 rounded-xl bg-white py-2 shadow-lg">
								{documents.document_type_1 ? (
									<Dropdown.Item
										className="px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50"
										onClick={() => handlePreviewClick('identity1')}
									>
										Identity Document 1
									</Dropdown.Item>
								) : null}
								{documents.document_type_2 ? (
									<Dropdown.Item
										className="px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50"
										onClick={() => handlePreviewClick('identity2')}
									>
										Identity Document 2
									</Dropdown.Item>
								) : null}
							</div>
						</Dropdown>
					) : (
						photoTrigger
					)}
				</div>

				<div className="dashboard-topbar-copy flex h-[64px] min-w-0 flex-1 flex-col justify-between overflow-hidden">
					<p className="dashboard-driver-name truncate text-[14px] font-semibold leading-tight text-[#475569]">{driverDisplayName}</p>
					<div className="flex-1 min-h-0 flex items-center">
						<OverflowMarquee text={pharmacyName} />
					</div>
					<p className="dashboard-time truncate text-[13px] font-semibold leading-tight text-[#64748b]">{currentDateAndTime}</p>
				</div>

				<div className="dashboard-topbar-actions flex h-[64px] shrink-0 flex-col items-center justify-between">
					<SOSemergencyEnableButton buttonClassName="dashboard-sos-button !mb-0 !h-[40px] !w-[40px] !rounded-[10px] !bg-red-600 !px-0 !text-[10px] !font-bold !text-white !shadow-none" />
					<button
						type="button"
						onClick={() => navigate(routeNames.DRIVER_DIGITAL_CARD)}
						className="dashboard-id-card-button flex h-[40px] w-[40px] items-center justify-center rounded-[10px] bg-transparent text-slate-950 transition hover:bg-slate-100"
						aria-label="Digital card"
					>
						<Lottie loop play animationData={idCardLottie} style={{ width: 30, height: 30 }} />
					</button>
				</div>
			</div>

			{isPreviewOpen ? (
				<Modal show={isPreviewOpen} onClose={closePreview} size="sm" popup dismissible>
					<Modal.Header className="px-6 pt-6 pb-0" />
					<Modal.Body className="px-6 pt-0 pb-6">
						<div className="relative w-full">
							{previewImageLoading ? (
								<div className="flex items-center justify-center rounded-lg bg-gray-50 p-8">
									<p className="text-center text-gray-500">Loading document...</p>
								</div>
							) : previewImage && !previewImageError ? (
								<img
									src={previewImage}
									alt="Identity Document"
									className="w-full rounded-lg shadow-sm"
									onError={() => setPreviewImageError(true)}
								/>
							) : (
								<div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-8">
									<p className="text-center text-gray-500">Image not available</p>
								</div>
							)}
						</div>
					</Modal.Body>
				</Modal>
			) : null}
		</>
	);
};
